import React, { useState, useRef, useEffect } from "react";
import { X, Send, Sparkles, RotateCcw, FileText, Loader } from "lucide-react";
import { getFedexCtx, subscribeFedexCtx } from "./fedexAssistantBus";
import { GEMINI_FLASH_MODEL } from "../utils/geminiModel";

/**
 * FedEx 검수 전용 AI 어시스턴트
 *
 * 범용 그리드 변환 패널(AIChatPanel)과 달리, 현재 FedEx 검수 화면에서
 * 보고 있는 파일의 추출 JSON·OCR 원문·규칙 위반 정보를 컨텍스트로 받아
 * "이 파일에 PO 어디 있어?", "운임 맞아?", "누락 필드 찾아줘" 같은
 * 검수 업무 질문에 실제 자료 기반으로 답한다.
 *
 * 백엔드는 기존 /api/ai/chat (Gemini) 를 그대로 재사용하고,
 * 매 질문마다 현재 파일 컨텍스트를 프롬프트에 주입한다.
 */
const QUICK_PROMPTS = [
  "이 파일에 PO 번호가 어디 있어?",
  "누락되었거나 비어있는 필드 찾아줘",
  "품목 합계가 인보이스 금액과 맞아?",
  "운임·보험료·핸들링 값이 문서와 일치해?",
  "각 품목의 HS코드(tariff)가 설명과 맞는지 봐줘",
];

const MIN_WIDTH = 340;
const MAX_WIDTH = 720;

const datasetLabel = (d) => (d === "IB" ? "수입" : d === "OB" ? "수출" : d || "-");

function buildPrompt(ctx, question) {
  if (!ctx) {
    return (
      "당신은 FedEx 통관 인보이스 검수를 돕는 AI입니다. " +
      "현재 선택된 파일이 없습니다. 사용자가 좌측에서 파일을 먼저 선택해야 함을 한국어로 안내하세요.\n\n질문: " +
      question
    );
  }
  let json = "";
  try { json = JSON.stringify(ctx.extracted, null, 2); } catch (_) { json = String(ctx.extracted); }
  return [
    "당신은 FedEx 통관 인보이스(상업송장) 검수를 돕는 AI입니다.",
    "아래는 사용자가 지금 검수 중인 실제 파일의 데이터입니다. 추측하지 말고 이 자료에만 근거해 한국어로 간결히 답하세요.",
    "값을 말할 때는 출처(JSON 필드명 또는 문서 OCR의 해당 문구)를 함께 알려주세요.",
    "자료에서 확인되지 않으면 \"문서/추출 데이터에서 확인되지 않음\"이라고 분명히 답하세요.",
    "",
    `[파일] ${ctx.name || "-"} (${datasetLabel(ctx.dataset)}, ${ctx.round || "-"}, 총 ${ctx.pageCount || 1}페이지)`,
    `[규칙 위반] ${ctx.rulesFailed && ctx.rulesFailed.length ? ctx.rulesFailed.join(", ") : "없음"}`,
    "",
    "[추출된 정형 데이터 JSON]",
    json || "(없음)",
    "",
    "[문서 OCR 원문 (전체 페이지)]",
    ctx.ocrText || "(OCR 텍스트 없음)",
    "",
    "---",
    `질문: ${question}`,
  ].join("\n");
}

const FedexAssistantPanel = ({ isOpen, onClose, apiBaseUrl = "http://localhost:3002" }) => {
  const [ctx, setCtx] = useState(() => getFedexCtx());
  const [messages, setMessages] = useState([]); // {role:'user'|'assistant', content}
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [panelWidth, setPanelWidth] = useState(400);
  const [isResizing, setIsResizing] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // 컨텍스트 버스 구독
  useEffect(() => {
    setCtx(getFedexCtx());
    return subscribeFedexCtx((c) => setCtx(c));
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isLoading]);

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 100);
  }, [isOpen]);

  // 리사이즈 (왼쪽 모서리 드래그)
  useEffect(() => {
    if (!isResizing) return;
    const onMove = (e) => {
      const w = window.innerWidth - e.clientX;
      setPanelWidth(Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, w)));
    };
    const onUp = () => { setIsResizing(false); document.body.style.cursor = ""; document.body.style.userSelect = ""; };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); };
  }, [isResizing]);

  const send = async (text) => {
    const question = (text ?? input).trim();
    if (!question || isLoading) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, { role: "user", content: question }]);
    setInput("");
    setIsLoading(true);
    try {
      const resp = await fetch(`${apiBaseUrl}/api/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: buildPrompt(getFedexCtx(), question),
          history,
          model: GEMINI_FLASH_MODEL,
        }),
      });
      const data = await resp.json();
      const reply = data?.reply || data?.error || "응답을 받지 못했습니다.";
      setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
    } catch (e) {
      setMessages((prev) => [...prev, { role: "assistant", content: `오류: ${e.message}` }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-full flex" style={{ width: panelWidth }}>
      {/* 리사이즈 핸들 */}
      <div
        onMouseDown={() => setIsResizing(true)}
        className="w-1 cursor-col-resize hover:bg-blue-300 bg-gray-200 flex-shrink-0"
        title="너비 조절"
      />
      <div className="flex-1 flex flex-col min-w-0 bg-white border-l shadow-xl">
        {/* 헤더 */}
        <div className="flex items-center gap-2 px-3 py-2.5 border-b flex-shrink-0">
          <Sparkles className="w-4 h-4 text-blue-500" />
          <span className="font-semibold text-sm text-gray-800">FedEx 검수 AI</span>
          <span className="text-[11px] text-gray-400">({panelWidth}px)</span>
          <div className="ml-auto flex items-center gap-1">
            <button onClick={() => setMessages([])} className="p-1 rounded hover:bg-gray-100 text-gray-500" title="대화 비우기">
              <RotateCcw className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-500" title="닫기">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* 현재 파일 컨텍스트 */}
        <div className="px-3 py-2 border-b bg-gray-50 flex-shrink-0 text-xs">
          {ctx ? (
            <div className="flex items-center gap-1.5 text-gray-600 min-w-0">
              <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              <span className="font-medium text-gray-700 truncate">{ctx.name}</span>
              <span className="text-gray-400">·</span>
              <span>{datasetLabel(ctx.dataset)}</span>
              <span className="text-gray-400">·</span>
              <span>{ctx.round}</span>
              {ctx.rulesFailed && ctx.rulesFailed.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 flex-shrink-0">
                  규칙 {ctx.rulesFailed.length}
                </span>
              )}
            </div>
          ) : (
            <span className="text-gray-400">좌측에서 검수할 파일을 선택하세요</span>
          )}
        </div>

        {/* 메시지 영역 */}
        <div className="flex-1 overflow-auto p-3 space-y-3">
          {messages.length === 0 && (
            <div className="space-y-2">
              <p className="text-xs text-gray-500">
                현재 파일의 추출 데이터·OCR 원문을 근거로 답해드려요. 예시 질문:
              </p>
              <div className="flex flex-col gap-1.5">
                {QUICK_PROMPTS.map((q) => (
                  <button
                    key={q}
                    onClick={() => send(q)}
                    disabled={!ctx}
                    className="text-left text-xs px-2.5 py-1.5 rounded border border-gray-200 hover:bg-blue-50 hover:border-blue-200 text-gray-700 disabled:opacity-40"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[88%] rounded-lg px-3 py-2 text-xs whitespace-pre-wrap break-words ${
                  m.role === "user" ? "bg-blue-500 text-white" : "bg-gray-100 text-gray-800"
                }`}
              >
                {m.content}
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 text-gray-500 rounded-lg px-3 py-2 text-xs flex items-center gap-1.5">
                <Loader className="w-3.5 h-3.5 animate-spin" /> 분석 중…
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* 입력창 */}
        <div className="p-2.5 border-t flex-shrink-0">
          <div className="flex items-end gap-1.5">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
              }}
              rows={1}
              placeholder={ctx ? "이 파일에 대해 물어보세요…" : "파일을 먼저 선택하세요"}
              className="flex-1 resize-none border rounded px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-300 max-h-28"
            />
            <button
              onClick={() => send()}
              disabled={isLoading || !input.trim()}
              className="p-2 rounded bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-40 flex-shrink-0"
              title="보내기 (Enter)"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FedexAssistantPanel;
